'use client'

import { Checkbox, CircularProgress, Text, VStack } from '@chakra-ui/react'
import { FormProvider } from 'react-hook-form'
import { OverridesContextProvider, type OverridesContextType, overrideText } from 'ui/lib/overrides'
import { type Language } from 'ecosystem'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useCheckoutContext } from '../../hooks'
import { type PostNordCheckoutProps } from './types'
import { PostNordDeliveryOptionCard } from './PostNordDeliveryOptionCard'
import { PostNordAddress } from './PostNordAddress'
import { CheckoutSectionHeading } from '../CheckoutSectionHeading'
import { PostNordPickupSelector } from './PostNordPickupSelector'
import { PostnordDeliveryMethodSelector } from './PostnordDeliveryMethodSelector'
import { usePostNordCheckout } from './usePostNordCheckout'
import { postnordCheckoutTranslations, type PostNordOverrides } from './translations'

const PostNordCheckoutComponent = (props?: PostNordCheckoutProps) => {
  const { containerProps, ...restProps } = props ?? {}

  const overrides = useOverridesContext<keyof PostNordOverrides>()
  const { shippingConfig, pickupOptions } = useCheckoutContext()

  const {
    form,
    selectedPickup,
    isPickupFromStore,
    isLoadingDeliveryOptions,
    deliveryOptionsData,
    onChangeDeliveryOption,
    isLoadingValidationPostalCode,
    postalCodePattern,
    isLoadingPostalCodePattern,
    getDeliveryOptionsRadioRootProps,
    getDeliveryOptionsRadioGroupRadioProps,
    isRequiredStreetAddress,
    isRememberOptedIn,
    setRememberOptedIn
  } = usePostNordCheckout({
    ...restProps,
    availableCountries: shippingConfig?.postnordOptions?.availableCountries
  })

  const renderDeliveryOptions = () => {
    if (isLoadingDeliveryOptions) {
      return <CircularProgress isIndeterminate />
    }

    if (!deliveryOptionsData?.warehouseToDeliveryOptions?.length) {
      return (
        <Text color="text.mild">
          {overrideText('Inga leveransalternativ tillgängliga.', overrides?.noDeliveryOptions)}
        </Text>
      )
    }

    return (
      <VStack gap={4} align="stretch" w="100%" {...getDeliveryOptionsRadioRootProps()}>
        {deliveryOptionsData?.warehouseToDeliveryOptions[0]?.deliveryOptions.map(
          (deliveryOption) => {
            return (
              <PostNordDeliveryOptionCard
                radioProps={getDeliveryOptionsRadioGroupRadioProps({ value: deliveryOption.id })}
                key={deliveryOption.id}
                item={deliveryOption}
                onChange={onChangeDeliveryOption}
              />
            )
          }
        )}
      </VStack>
    )
  }

  return (
    <FormProvider {...form}>
      <VStack w="100%">
        <VStack w="100%" maxW="600px" gap={6} align="stretch" pt={6} {...containerProps}>
          <PostnordDeliveryMethodSelector
            name="isPickupFromStore"
            overrides={{
              pickUpInStore: overrides?.pickUpInStore,
              pickUpInStoreDescription: overrides?.pickUpInStoreDescription,
              deliveryOption: overrides?.deliveryOption,
              deliveryOptionDescription: overrides?.deliveryOptionDescription
            }}
          />

          {isPickupFromStore ? (
            <PostNordPickupSelector
              selectedPickup={selectedPickup}
              pickupOptions={pickupOptions || []}
              overrides={{
                pickupOptionsTitle: overrides?.pickupOptionsTitle
              }}
            />
          ) : (
            <>
              <VStack w="100%" align="stretch">
                <CheckoutSectionHeading>
                  {overrideText('Mottagaruppgifter', overrides?.recipientTitle)}
                </CheckoutSectionHeading>

                <PostNordAddress
                  isPostalCodeLoading={isLoadingValidationPostalCode || isLoadingPostalCodePattern}
                  postalCodePattern={postalCodePattern}
                  isRequiredStreetAddress={isRequiredStreetAddress}
                  availableCountries={shippingConfig?.postnordOptions?.availableCountries}
                />

                {/* The shopper's explicit request to have these details kept on this
                    device — nothing is stored until it is ticked. */}
                <Checkbox
                  borderColor="primary"
                  isChecked={isRememberOptedIn}
                  mt={2}
                  onChange={(event) => setRememberOptedIn(event.target.checked)}
                  size="md"
                >
                  {overrideText(
                    'Kom ihåg mina uppgifter på den här enheten',
                    overrides?.rememberOnDevice
                  )}
                </Checkbox>
              </VStack>

              <VStack w="100%" align="stretch">
                <CheckoutSectionHeading>
                  {overrideText('Leveransalternativ', overrides?.deliveryOptionsTitle)}
                </CheckoutSectionHeading>
                {renderDeliveryOptions()}
              </VStack>
            </>
          )}
        </VStack>
      </VStack>
    </FormProvider>
  )
}

export const PostNordCheckout = (props?: PostNordCheckoutProps) => {
  const lang = document.documentElement.lang ?? 'sv'

  return (
    <OverridesContextProvider
      overrides={postnordCheckoutTranslations[lang as Language] as unknown as OverridesContextType}
    >
      <PostNordCheckoutComponent {...props} />
    </OverridesContextProvider>
  )
}
